import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

export default function SurveyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/surveys/${id}`)
      .then(({ data }) => {
        setSurvey(data);
        setAnswers(new Array(data.questions.length).fill(-1));
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  if (!survey) return <p className="text-plum-700/50 italic">Chargement...</p>;

  const allAnswered = answers.every((a) => a >= 0);
  const completed = answers.filter((a) => a >= 0).length;
  const pct = Math.round((completed / survey.questions.length) * 100);

  const submit = async () => {
    if (!allAnswered) return toast.error("Veuillez répondre à toutes les questions");
    setBusy(true);
    try {
      const { data } = await api.post(`/surveys/${id}/respond`, { answers });
      window.dispatchEvent(new CustomEvent("istres-action-completed", { detail: { kind: "survey-response" } }));
      toast.success(`Merci. +${data.points_earned} points`);
      navigate("/sondages");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl space-y-12">
      <button onClick={() => navigate(-1)} className="btn-ghost-line text-xs">
        <ArrowLeft size={12} /> Retour
      </button>

      <header>
        <div className="phenix-eyebrow mb-3">Sondage</div>
        <h1 className="heading-display text-2xl text-plum-900 mb-4">{survey.title}</h1>
        <p className="text-base text-plum-700/70 leading-relaxed max-w-2xl">{survey.description}</p>
      </header>

      <div className="flex items-center gap-6 border-y border-cream-200 py-5">
        <div className="phenix-eyebrow shrink-0">Progression</div>
        <Progress value={pct} className="h-px flex-1 bg-ink-900/10" />
        <span className="font-mono text-xs text-plum-700 tabular-nums">
          {String(completed).padStart(2, "0")}/{String(survey.questions.length).padStart(2, "0")}
        </span>
      </div>

      <div className="space-y-12" data-testid="survey-questions">
        {survey.questions.map((q, qi) => (
          <div key={qi} className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-3">
              <div className="font-mono text-[11px] text-plum-700/50 mb-2">
                {String(qi + 1).padStart(2, "0")} / {String(survey.questions.length).padStart(2, "0")}
              </div>
            </div>
            <div className="lg:col-span-9">
              <Label className="heading-display text-base text-plum-900 mb-6 block">
                {q.question}
              </Label>
              <RadioGroup
                value={answers[qi] >= 0 ? String(answers[qi]) : ""}
                onValueChange={(v) => {
                  const next = [...answers]; next[qi] = parseInt(v); setAnswers(next);
                }}
                className="divide-y divide-cream-200 border-y border-cream-200"
              >
                {q.options.map((opt, oi) => (
                  <label
                    key={oi}
                    className={`flex items-center gap-5 py-4 cursor-pointer hover:bg-cream-50 transition-colors px-2 -mx-2 ${
                      answers[qi] === oi ? "text-ink-900" : "text-plum-700/70"
                    }`}
                  >
                    <RadioGroupItem
                      value={String(oi)} id={`q${qi}-${oi}`}
                      data-testid={`survey-option-${qi}-${oi}`}
                    />
                    <span className="font-mono text-[11px] text-plum-700/50 w-6">
                      {String.fromCharCode(65 + oi)}
                    </span>
                    <span className="font-display text-lg flex-1">{opt}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-cream-200 pt-8">
        <Button
          onClick={submit} disabled={busy || !allAnswered}
          className="btn-pill bg-plum-600 hover:bg-plum-700 text-white h-12 px-8 group disabled:opacity-30"
          data-testid="survey-submit-btn"
        >
          Envoyer mes réponses
          <ArrowUpRight size={14} className="ml-2 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Button>
      </div>
    </div>
  );
}
